import { SplitTypeWrapper } from "../../components/SplitTypeWrapper";
import { getGsap, getScrollTrigger } from "../global/GsapGlobals";
import { DUR, EASE, STAGGER, motionOK } from "../global/MotionTokens";

const HERO_TITLE_SELECTOR = '[data-anim="hero-title-stagger"]';
const SECTION_TITLE_SELECTOR = [
    "main section h2.no-heading-xl",
    "main section h2.no-heading-lg",
    "main section h2.no-display-sm",
].join(",");
const EXCLUDED_ANCESTOR_SELECTOR = [
    "[data-section]",
    "[data-no-section-title-reveal]",
    "dialog",
    "[role='dialog']",
    ".modal",
].join(",");
const DECORATED_TITLE_SELECTOR = ".section-head__mark";
const HERO_SUPPORT_SELECTOR = [
    "[class*='__eyebrow']",
    "[class*='__subtitle']",
    "[class*='__desc']",
    "[class*='__lead']",
    "[class*='__summary']",
].join(",");

export class HeroTitleStaggerController {
    static init(): void {
        const heroTitles = Array.from(
            document.querySelectorAll<HTMLElement>(HERO_TITLE_SELECTOR),
        );
        const sectionTitles = Array.from(
            document.querySelectorAll<HTMLElement>(SECTION_TITLE_SELECTOR),
        ).filter((title) => HeroTitleStaggerController.canAnimateSectionTitle(title));

        sectionTitles.forEach((title) => {
            title.dataset.anim = "section-title-stagger";
        });

        const titles = Array.from(new Set([...heroTitles, ...sectionTitles]));
        if (!titles.length) return;
        if (!motionOK()) return;

        const gsap = getGsap();
        const ScrollTrigger = getScrollTrigger();

        titles.forEach((title) => {
            if (title.dataset.titleStaggerBound === "1") return;
            title.dataset.titleStaggerBound = "1";

            const chars = SplitTypeWrapper.splitTitle(title).chars;
            if (!chars.length) return;

            gsap.set(title, { opacity: 1 });
            gsap.set(chars, {
                yPercent: 112,
                opacity: 0,
                willChange: "transform, opacity",
            });
            title.classList.add("is-title-stagger-ready");
            title.classList.add("is-hero-title-stagger-ready");

            const isSectionTitle = title.dataset.anim === "section-title-stagger";
            const triggerRoot = title.closest<HTMLElement>("section, header") ?? title;
            const supportText = isSectionTitle
                ? []
                : Array.from(triggerRoot.querySelectorAll<HTMLElement>(HERO_SUPPORT_SELECTOR))
                    .filter((node) => !node.contains(title))
                    .filter((node) => node.closest("section, header") === triggerRoot)
                    .filter((node) => !node.hasAttribute("data-anim"));

            if (supportText.length) {
                supportText.forEach((node) => {
                    node.setAttribute("data-no-auto-aos", "");
                });
                gsap.set(supportText, {
                    y: 24,
                    opacity: 0,
                    willChange: "transform, opacity",
                });
            }

            const timeline = gsap.timeline({
                paused: true,
            });

            timeline.to(chars, {
                yPercent: 0,
                opacity: 1,
                duration: DUR.slower,
                ease: EASE.outExpo,
                stagger: STAGGER.fast,
                onComplete: () => {
                    gsap.set(chars, { willChange: "auto" });
                },
            });

            if (supportText.length) {
                timeline.to(supportText, {
                    y: 0,
                    opacity: 1,
                    duration: DUR.slow,
                    ease: EASE.outQuart,
                    stagger: STAGGER.base,
                    onComplete: () => {
                        gsap.set(supportText, { willChange: "auto" });
                    },
                }, "<35%");
            }

            ScrollTrigger.create({
                trigger: isSectionTitle ? title : triggerRoot,
                start: "top 82%",
                once: true,
                invalidateOnRefresh: true,
                onEnter: () => {
                    if (title.dataset.titleStaggerPlayed === "1") return;
                    title.dataset.titleStaggerPlayed = "1";
                    timeline.play(0);
                },
            });
        });
    }

    private static canAnimateSectionTitle(title: HTMLElement): boolean {
        if (title.hasAttribute("data-anim")) return false;
        if (title.closest(EXCLUDED_ANCESTOR_SELECTOR)) return false;
        if (title.querySelector(DECORATED_TITLE_SELECTOR)) return false;
        return true;
    }
}
